import { useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import type { AsignacionEspacioLocal, RecursoHorarioLocal, RecursoLocal } from '../datos/db.js'
import { hoy } from '../datos/formato.js'
import { Boton } from '../comunes/Boton.js'
import {
  DIAS,
  aMinutos,
  aReloj,
  asignacionCubreFecha,
  huecosEntreTramos,
  rangoHoras,
} from './elegibles.js'

/**
 * Día por consultorios (solo lectura).
 *
 * Un día concreto, consultorios como columnas: la vista que muestra de un
 * vistazo cómo se reparte el equipo y dónde quedan huecos. Los huecos entre
 * dos tramos publicados se pintan distintos de "libre": el hueco es una
 * imperfección de la plantilla, no una franja disponible.
 */

const PX_POR_HORA = 56

export function DiaConsultorios({
  espacios,
  personas,
  horarios,
  asignaciones,
}: {
  espacios: RecursoLocal[]
  personas: RecursoLocal[]
  horarios: RecursoHorarioLocal[]
  asignaciones: AsignacionEspacioLocal[]
}) {
  const [dia, setDia] = useState(hoy())

  const diaSemana = useMemo(() => new Date(`${dia}T12:00:00`).getDay(), [dia])
  const horas = useMemo(() => {
    const { desde, hasta } = rangoHoras(horarios, 7, 23)
    const lista: number[] = []
    for (let h = desde; h < hasta; h++) lista.push(h)
    return { desde, hasta, lista }
  }, [horarios])

  const nombreDe = useMemo(() => {
    const mapa = new Map(personas.map((p) => [p.id, p.nombre]))
    return (id: string) => mapa.get(id) ?? 'Persona'
  }, [personas])

  // Las filas publicadas de cada consultorio para la fecha que se mira.
  const porEspacio = useMemo(() => {
    const publicadas = asignaciones.filter(
      (a) => a.estado === 'publicada' && !a.deleted_at && asignacionCubreFecha(a, dia),
    )
    const mapa = new Map<string, AsignacionEspacioLocal[]>()
    for (const a of publicadas) {
      const lista = mapa.get(a.espacio_id) ?? []
      lista.push(a)
      mapa.set(a.espacio_id, lista)
    }
    return mapa
  }, [asignaciones, dia])

  function cambiarDia(delta: number) {
    const dt = DateTime.fromISO(dia, { zone: 'America/Asuncion' }).plus({ days: delta })
    setDia(dt.toISODate() ?? dia)
  }

  const baseMin = horas.desde * 60
  const baseMax = horas.hasta * 60
  const altoGrilla = horas.lista.length * PX_POR_HORA

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="txt-seccion">
            {DIAS[diaSemana]}, día por consultorios
          </h2>
          <p className="txt-secundario">Solo lectura: la franja publicada de cada consultorio.</p>
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => cambiarDia(-1)}
            className="flex h-9 items-center px-2 text-slate-500 hover:text-slate-900"
            aria-label="Día anterior"
          >
            ‹
          </button>
          <input
            type="date"
            value={dia}
            onChange={(ev) => setDia(ev.target.value)}
            className="h-9 border-l border-r border-slate-200 px-2 text-sm"
          />
          <button
            type="button"
            onClick={() => cambiarDia(1)}
            className="flex h-9 items-center px-2 text-slate-500 hover:text-slate-900"
            aria-label="Día siguiente"
          >
            ›
          </button>
          <Boton variante="ghost" tamano="sm" onClick={() => setDia(hoy())}>
            Hoy
          </Boton>
        </div>
      </header>

      {/* Leyenda */}
      <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm border border-slate-200 bg-white" /> Libre
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-teal-700" /> Asignado
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-3 w-3 rounded-sm border border-amber-400"
            style={{
              background:
                'repeating-linear-gradient(-45deg, #fde68a 0 4px, #fbbf24 4px 8px)',
            }}
          />
          Hueco entre franjas
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <div className="flex min-w-[720px]">
          <div className="w-16 shrink-0 border-r border-slate-200">
            <div className="h-10" />
            {horas.lista.map((h) => (
              <div
                key={h}
                className="flex items-start justify-end pr-2 text-xs text-slate-400"
                style={{ height: PX_POR_HORA }}
              >
                <span className="mt-[-6px] tabular-nums">{String(h).padStart(2, '0')}:00</span>
              </div>
            ))}
          </div>

          {espacios.map((espacio) => {
            const tramos = (porEspacio.get(espacio.id) ?? [])
              .map((a) => ({ desde: aMinutos(a.desde), hasta: aMinutos(a.hasta) }))
              .filter((t) => t.hasta > baseMin && t.desde < baseMax)
            const huecos = huecosEntreTramos(tramos).filter(
              (h) => h.desde >= baseMin && h.hasta <= baseMax,
            )

            return (
              <div
                key={espacio.id}
                className="min-w-0 flex-1 border-r border-slate-200 last:border-r-0"
              >
                <div className="border-b border-slate-200 px-2 py-2 text-center text-sm font-medium text-slate-700">
                  {espacio.nombre}
                </div>
                <div className="relative" style={{ height: altoGrilla }}>
                  {horas.lista.map((h) => (
                    <div
                      key={h}
                      className="absolute left-0 right-0 border-b border-slate-100"
                      style={{
                        top: (h - horas.desde) * PX_POR_HORA,
                        height: PX_POR_HORA,
                      }}
                    />
                  ))}

                  {(porEspacio.get(espacio.id) ?? []).map((a) => {
                    const desde = Math.max(baseMin, aMinutos(a.desde))
                    const hasta = Math.min(baseMax, aMinutos(a.hasta))
                    if (hasta <= desde) return null
                    const top = ((desde - baseMin) / 60) * PX_POR_HORA
                    const alto = ((hasta - desde) / 60) * PX_POR_HORA
                    return (
                      <div
                        key={a.id}
                        className="absolute left-0.5 right-0.5 overflow-hidden rounded-md bg-teal-700 px-1.5 py-0.5 text-xs text-white shadow-sm"
                        style={{ top, height: Math.max(20, alto), zIndex: 10 }}
                      >
                        <p className="truncate font-medium">{nombreDe(a.persona_id)}</p>
                        {alto > PX_POR_HORA && (
                          <p className="truncate tabular-nums text-teal-100">
                            {aReloj(desde)}–{aReloj(hasta)}
                          </p>
                        )}
                      </div>
                    )
                  })}

                  {huecos.map((h, i) => {
                    const top = ((h.desde - baseMin) / 60) * PX_POR_HORA
                    const alto = ((h.hasta - h.desde) / 60) * PX_POR_HORA
                    return (
                      <div
                        key={`hueco-${h.desde}-${i}`}
                        className="absolute left-0.5 right-0.5 overflow-hidden rounded-md border border-amber-400 py-0.5 text-center text-[11px] font-medium text-amber-800"
                        style={{
                          top,
                          height: Math.max(14, alto),
                          zIndex: 5,
                          background:
                            'repeating-linear-gradient(-45deg, #fde68a 0 6px, #fbbf24 6px 12px)',
                        }}
                      >
                        {alto >= PX_POR_HORA && (
                          <span className="bg-white/40 px-1 rounded">{aReloj(h.desde)}</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {espacios.length === 0 && (
        <p className="mt-4 rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-500">
          No hay consultorios configurados.
        </p>
      )}
    </section>
  )
}